import { useEffect, useState, useCallback } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Button,
  Spinner,
  Form,
  Input,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
} from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../../helpers/api';
import { uploadServiceContractFiles, validateFiles } from '../../helpers/fileUpload';
import PermissionGuard from '../../components/Common/PermissionGuard';
import usePermission from '../../Hooks/usePermission';
import { AssetListTable } from '../../components/Common';
import {
  DOCUMENT_VAULT_CREATE,
  DOCUMENT_VAULT_UPDATE,
  DOCUMENT_VAULT_DELETE,
} from '../../constants/permissions';

const PAGE_SIZE = 10;

const formatDateForAPI = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const formatDateForDisplay = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const emptyForm = () => ({
  id: null,
  name: '',
  startDate: '',
  endDate: '',
  vendorId: '',
  assetIds: [],
  documentUrls: [],
  notes: '',
});

const Certificates = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin } = usePermission();

  const currentUser = JSON.parse(localStorage.getItem('authUser') || '{}');
  const organizationId = currentUser?.activeOrganizationId ?? null;

  const [documents, setDocuments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [assets, setAssets] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [count, setCount] = useState(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(true);

  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [form, setForm] = useState(emptyForm());
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const [errors, setErrors] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);

  const [assetSearchTerm, setAssetSearchTerm] = useState('');
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);

  const [detailsTab, setDetailsTab] = useState('info');

  document.title = `${t('compliance.tabs.certificates', 'Certificates')} | Arcmedix`;

  // ---------- fetchers ----------
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setApiError('');
    try {
      const params = { count };
      if (vendorFilter) params.vendorId = vendorFilter;
      if (searchTerm.trim()) params.searchTerm = searchTerm.trim();
      const { data } = await api.get('certificates/get-all', { params });
      const list = data?.response || [];
      setDocuments(list);
      setHasMore(list.length >= count);
    } catch (err) {
      console.error('Error fetching document vault:', err);
      setApiError(t('business.document_vault.error', 'Could not load certificates.'));
    } finally {
      setLoading(false);
    }
  }, [count, vendorFilter, searchTerm, t]);

  const fetchVendors = useCallback(async () => {
    try {
      const { data } = await api.get('vendor/getAllVendors');
      setVendors(data?.response || []);
    } catch (err) {
      console.error('Error fetching vendors:', err);
      setVendors([]);
    }
  }, []);

  const searchAssets = useCallback(async (term) => {
    if (!organizationId || !term) {
      setAssets([]);
      return;
    }
    try {
      const params = {
        organizationId: organizationId,
        count: 5,
        search: term,
        hierarchy: false,
      };
      const { data } = await api.get('asset/all', { params });
      setAssets(data?.response?.[0]?.assets || []);
    } catch (err) {
      console.error('Error searching assets:', err);
      setAssets([]);
    }
  }, [organizationId]);

  // ---------- effects ----------
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    if (organizationId) fetchVendors();
  }, [organizationId, fetchVendors]);

  useEffect(() => {
    if (assetSearchTerm) searchAssets(assetSearchTerm);
    else setAssets([]);
  }, [assetSearchTerm, searchAssets]);

  // ---------- handlers ----------
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCount(PAGE_SIZE);
  };

  const handleVendorFilterChange = (e) => {
    setVendorFilter(e.target.value);
    setCount(PAGE_SIZE);
  };

  const handleLoadMore = () => setCount((c) => c + PAGE_SIZE);

  const openCreateModal = () => {
    setForm(emptyForm());
    setSelectedAssets([]);
    setErrors({});
    setApiError('');
    setUploadErrors([]);
    setModalOpen(true);
  };

  const openEditModal = (doc) => {
    setForm({
      id: doc.id,
      name: doc.name || '',
      startDate: doc.startDate ? doc.startDate.slice(0, 10) : '',
      endDate: doc.endDate ? doc.endDate.slice(0, 10) : '',
      vendorId: doc.vendorId || '',
      assetIds: doc.assetIds || [],
      documentUrls: doc.documentUrls || [],
      notes: doc.notes || '',
    });
    setSelectedAssets(
      (doc.assets || []).map((a) => ({
        id: a.id,
        assetName: a.assetName || a.name,
        serialNumber: a.serialNumber,
      })),
    );
    setErrors({});
    setApiError('');
    setUploadErrors([]);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm(emptyForm());
    setSelectedAssets([]);
    setAssetSearchTerm('');
    setShowAssetDropdown(false);
  };

  const openDetailsModal = (doc) => {
    setSelectedDocument(doc);
    setDetailsTab('info');
    setDetailsOpen(true);
  };

  const handleAssetClick = async (asset) => {
    if (!asset) return;
    // Fast path: uuid was included in the embedded snapshot.
    if (asset.uuid) {
      setDetailsOpen(false);
      navigate(`/asset-detail/${asset.uuid}`);
      return;
    }
    // Fallback: snapshot didn't carry a uuid. Resolve it by id, then navigate.
    if (!asset.id) {
      console.warn('handleAssetClick: asset has neither uuid nor id', asset);
      return;
    }
    try {
      const { data } = await api.get('asset/get-by-id', { params: { id: asset.id } });
      const uuid = data?.response?.uuid;
      if (!uuid) {
        console.warn('handleAssetClick: backend returned no uuid for asset', asset.id);
        return;
      }
      setDetailsOpen(false);
      navigate(`/asset-detail/${uuid}`);
    } catch (err) {
      console.error('handleAssetClick: lookup failed', err);
    }
  };

  const openDeleteModal = (doc) => {
    setSelectedDocument(doc);
    setDeleteOpen(true);
  };

  const handleField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSelectAsset = (asset) => {
    if (selectedAssets.some((a) => a.id === asset.id)) return;
    setSelectedAssets((prev) => [...prev, asset]);
    setForm((prev) => ({
      ...prev,
      assetIds: [...(prev.assetIds || []), asset.id],
    }));
    setAssetSearchTerm('');
    setShowAssetDropdown(false);
  };

  const handleRemoveAsset = (assetId) => {
    setSelectedAssets((prev) => prev.filter((a) => a.id !== assetId));
    setForm((prev) => ({
      ...prev,
      assetIds: (prev.assetIds || []).filter((id) => id !== assetId),
    }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const validation = validateFiles(files);
    if (validation.errors.length > 0) {
      setUploadErrors(validation.errors);
      setTimeout(() => setUploadErrors([]), 5000);
    }
    if (validation.validFiles.length === 0) {
      e.target.value = '';
      return;
    }
    setUploadingFiles(true);
    try {
      const urls = await uploadServiceContractFiles(validation.validFiles);
      setForm((prev) => ({
        ...prev,
        documentUrls: [...(prev.documentUrls || []), ...urls],
      }));
      if (errors.documentUrls) setErrors((prev) => ({ ...prev, documentUrls: '' }));
    } catch (err) {
      console.error('Failed to upload files:', err);
      setApiError(t('shared.failed_upload', 'Failed to upload files. Please try again.'));
    } finally {
      setUploadingFiles(false);
      e.target.value = '';
    }
  };

  const handleRemoveFile = (index) => {
    setForm((prev) => ({
      ...prev,
      documentUrls: (prev.documentUrls || []).filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.name?.trim()) {
      newErrors.name = t('business.document_vault.validation.name_required', 'Name is required.');
    }
    // Dates are optional. Only enforce ordering when both are supplied.
    if (form.startDate && form.endDate && new Date(form.endDate) < new Date(form.startDate)) {
      newErrors.endDate = t('business.document_vault.validation.end_after_start', 'End date must be on or after start date.');
    }
    if (!form.documentUrls || form.documentUrls.length === 0) {
      newErrors.documentUrls = t('business.document_vault.validation.file_required', 'At least one file is required.');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setApiError('');
    try {
      const payload = {
        ...form,
        startDate: formatDateForAPI(form.startDate),
        endDate: formatDateForAPI(form.endDate),
        vendorId: form.vendorId || null,
        organizationId: organizationId,
      };
      if (form.id) {
        await api.put('certificates/update', payload);
      } else {
        await api.post('certificates/create', payload);
      }
      await fetchDocuments();
      closeModal();
    } catch (err) {
      console.error('Error saving document:', err);
      setApiError(err?.response?.data?.error || t('business.document_vault.toast.save_failed', 'Could not save certificate.'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDocument?.id) return;
    setLoading(true);
    setApiError('');
    try {
      await api.delete(`certificates/delete/${selectedDocument.id}`);
      await fetchDocuments();
      setDeleteOpen(false);
      setSelectedDocument(null);
    } catch (err) {
      console.error('Error deleting document:', err);
      setApiError(err?.response?.data?.error || t('business.document_vault.toast.delete_failed', 'Could not delete certificate.'));
    } finally {
      setLoading(false);
    }
  };

  const canDeleteDocument = (doc) => {
    if (isAdmin) return true;
    const createdById = doc?.createdBy?.id ?? doc?.createdBy;
    if (createdById === undefined || createdById === null) return true;
    return createdById === currentUser?.id;
  };

  // ---------- presentation helpers ----------
  const renderStatusBadge = (doc) => {
    const status = doc.status;
    const days = doc.daysUntilExpiry;
    if (status === 'EXPIRED') {
      return <span className="badge bg-danger">{t('business.document_vault.status.expired', 'Expired')}</span>;
    }
    if (status === 'EXPIRING_SOON') {
      return (
        <span className="badge bg-warning text-dark">
          {t('business.document_vault.status.expires_in_days', 'Expires in {{days}} days', { days })}
        </span>
      );
    }
    return <span className="badge bg-success">{t('business.document_vault.status.active', 'Active')}</span>;
  };

  const fileIconForUrl = (url) => {
    if (/\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?|$)/i.test(url)) return 'image';
    if (/\.pdf(\?|$)/i.test(url)) return 'file-text';
    if (/\.(doc|docx)(\?|$)/i.test(url)) return 'file-text';
    if (/\.(xls|xlsx|csv)(\?|$)/i.test(url)) return 'grid';
    return 'file';
  };

  const fileNameFromUrl = (url) => url.split('/').pop().split('?')[0];

  // ---------- render ----------
  return (
    <div className="page-content mt-4">
      <div className="container-fluid">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3 flex-wrap"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <PermissionGuard permission={DOCUMENT_VAULT_CREATE}>
            <button
              type="button"
              className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2"
              onClick={openCreateModal}
            >
              <FeatherIcon icon="plus" size={18} />
              {t('business.document_vault.add', 'Add Certificate')}
            </button>
          </PermissionGuard>

          <div style={{ minWidth: 280, flexGrow: 1, maxWidth: 420 }}>
            <Input
              type="text"
              placeholder={t('business.document_vault.search_placeholder', 'Search by name or vendor...')}
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          <div style={{ minWidth: 220 }}>
            <Input type="select" value={vendorFilter} onChange={handleVendorFilterChange}>
              <option value="">{t('business.document_vault.filter.all_vendors', 'All vendors')}</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.vendorName}
                </option>
              ))}
            </Input>
          </div>
        </motion.div>

        {apiError && !modalOpen && (
          <Alert color="danger" className="mb-3">{apiError}</Alert>
        )}

        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center">
            <h3 className="card-title text-success mb-0">
              {t('compliance.tabs.certificates', 'Certificates')}
            </h3>
          </CardHeader>
          <CardBody>
            <div
              className="table-responsive"
              style={{
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                maxHeight: 'calc(100vh - 300px)',
                overflowY: 'auto',
              }}
            >
              <table
                className="table table-hover mb-0"
                style={{ borderSpacing: '0.1rem', borderCollapse: 'separate' }}
              >
                <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                  <tr>
                    <th>#</th>
                    <th>{t('business.document_vault.field.name', 'Certificate name')}</th>
                    <th>{t('business.document_vault.column.expiry_date', 'Expiry date')}</th>
                    <th>{t('shared.status', 'Status')}</th>
                    <th>{t('business.document_vault.field.vendor', 'Vendor')}</th>
                    <th>{t('business.document_vault.field.assets', 'Assets')}</th>
                    <th>{t('business.document_vault.field.files', 'Files')}</th>
                    <th>{t('shared.delete', 'Delete')}</th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {documents.map((doc, ind) => (
                      <motion.tr
                        key={doc.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.25 }}
                      >
                        <th scope="row" style={{ verticalAlign: 'middle' }}>
                          {ind + 1}
                        </th>
                        <td
                          className="text-primary"
                          role="button"
                          onClick={() => openDetailsModal(doc)}
                          style={{ cursor: 'pointer', textDecoration: 'underline', verticalAlign: 'middle' }}
                        >
                          {doc.name}
                        </td>
                        <td style={{ verticalAlign: 'middle' }}>
                          {formatDateForDisplay(doc.endDate)}
                        </td>
                        <td style={{ verticalAlign: 'middle' }}>
                          {renderStatusBadge(doc)}
                        </td>
                        <td style={{ verticalAlign: 'middle' }}>
                          {doc.vendorName || '-'}
                        </td>
                        <td className="text-center text-muted" style={{ verticalAlign: 'middle' }}>
                          {doc.assets?.length ?? 0}
                        </td>
                        <td style={{ verticalAlign: 'middle' }}>
                          <FeatherIcon icon="paperclip" size={14} className="me-1 text-muted" />
                          {(doc.documentUrls || []).length}
                        </td>
                        <td style={{ verticalAlign: 'middle' }}>
                          {canDeleteDocument(doc) && (
                            <PermissionGuard permission={DOCUMENT_VAULT_DELETE}>
                              <span
                                className="text-danger"
                                role="button"
                                onClick={() => openDeleteModal(doc)}
                              >
                                <FeatherIcon icon="trash-2" />
                              </span>
                            </PermissionGuard>
                          )}
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>

              {loading && (
                <div className="text-center py-3">
                  <Spinner color="primary" />
                </div>
              )}
              {!loading && documents.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-muted mb-0">
                    {searchTerm || vendorFilter
                      ? t('business.document_vault.no_results', 'No certificates match your search.')
                      : t('business.document_vault.empty', 'No certificates yet.')}
                  </p>
                </div>
              )}
            </div>

            {hasMore && documents.length > 0 && !loading && !searchTerm && (
              <div className="text-center mt-3">
                <Button color="primary" onClick={handleLoadMore} disabled={loading}>
                  {loading ? t('shared.loading', 'Loading...') : t('shared.load', 'Load More')}
                </Button>
              </div>
            )}
            {!hasMore && documents.length > 0 && !loading && !searchTerm && (
              <div className="text-center py-2 mt-3 text-muted">
                {t('business.document_vault.no_more', 'No more certificates to load')}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Create / Edit modal */}
      <Modal isOpen={modalOpen} toggle={closeModal} size="lg" className="modal-dialog-centered">
        <ModalHeader toggle={closeModal}>
          {form.id
            ? t('business.document_vault.edit', 'Edit Certificate')
            : t('business.document_vault.add', 'Add Certificate')}
        </ModalHeader>
        <ModalBody>
          {apiError && <Alert color="danger">{apiError}</Alert>}
          <Form onSubmit={handleSubmit}>
            <Row className="mb-4 g-3">
              <Col md={12}>
                <div className="form-group">
                  <label htmlFor="dv-name" className="form-label fw-medium">
                    {t('business.document_vault.field.name', 'Certificate name')}
                    <span className="text-danger ms-1">*</span>
                  </label>
                  <Input
                    id="dv-name"
                    type="text"
                    value={form.name}
                    invalid={!!errors.name}
                    onChange={(e) => handleField('name', e.target.value)}
                    placeholder={t('business.document_vault.field.name_placeholder', 'Enter certificate name')}
                  />
                  {errors.name && <div className="text-danger small mt-1">{errors.name}</div>}
                </div>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={12}>
                <div className="form-group">
                  <label className="form-label fw-medium">
                    {t('business.document_vault.field.files', 'Files')}
                    <span className="text-danger ms-1">*</span>
                  </label>
                  {uploadErrors.length > 0 && (
                    <div className="mb-2">
                      {uploadErrors.map((err, idx) => (
                        <Alert key={idx} color="danger" className="py-2 mb-1">
                          <small>{err}</small>
                        </Alert>
                      ))}
                    </div>
                  )}
                  <div
                    className="border rounded p-4 text-center"
                    style={{
                      borderStyle: 'dashed',
                      borderWidth: '2px',
                      borderColor: '#dee2e6',
                      backgroundColor: '#fafbfc',
                    }}
                  >
                    <input
                      type="file"
                      id="dv-file-upload"
                      className="d-none"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                      multiple
                      disabled={uploadingFiles}
                      onChange={handleFileUpload}
                    />
                    <label
                      htmlFor="dv-file-upload"
                      className={`btn btn-outline-secondary mb-2 ${uploadingFiles ? 'disabled' : ''}`}
                      style={{ cursor: uploadingFiles ? 'not-allowed' : 'pointer' }}
                    >
                      {uploadingFiles ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          {t('shared.uploading', 'Uploading...')}
                        </>
                      ) : (
                        <>
                          <FeatherIcon icon="paperclip" size={16} className="me-2" />
                          {t('shared.attach_files', 'Attach Files')}
                        </>
                      )}
                    </label>
                    <p className="text-muted small mb-0">
                      {uploadingFiles
                        ? t('shared.wait_uploading_files', 'Please wait while files are being uploaded...')
                        : t('business.document_vault.field.upload_help', 'PDF, Word, Excel, CSV, or image. Max 50 MB per file.')}
                    </p>
                  </div>
                  {errors.documentUrls && (
                    <div className="text-danger small mt-1">{errors.documentUrls}</div>
                  )}
                  {form.documentUrls && form.documentUrls.length > 0 && (
                    <div className="mt-3">
                      {form.documentUrls.map((url, index) => (
                        <div
                          key={index}
                          className="d-flex align-items-center justify-content-between bg-light rounded px-3 py-2 mb-2"
                        >
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="d-flex align-items-center text-decoration-none"
                            style={{ minWidth: 0, flex: 1 }}
                          >
                            <FeatherIcon icon={fileIconForUrl(url)} size={18} className="text-primary me-2" />
                            <span className="text-truncate" style={{ maxWidth: 300 }}>
                              {fileNameFromUrl(url)}
                            </span>
                          </a>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleRemoveFile(index)}
                          >
                            <FeatherIcon icon="trash-2" size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            <Row className="mb-4 g-3">
              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="dv-start-date" className="form-label fw-medium">
                    {t('business.document_vault.field.start_date', 'Start date')}
                  </label>
                  <DatePicker
                    id="dv-start-date"
                    selected={form.startDate ? new Date(form.startDate) : null}
                    onChange={(d) => handleField('startDate', d ? d.toISOString().slice(0, 10) : '')}
                    dateFormat="dd/MM/yyyy"
                    className={`form-control ${errors.startDate ? 'is-invalid' : ''}`}
                    wrapperClassName="w-100"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    placeholderText="dd/mm/yyyy"
                    isClearable
                  />
                  {errors.startDate && <div className="text-danger small mt-1">{errors.startDate}</div>}
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group">
                  <label htmlFor="dv-end-date" className="form-label fw-medium">
                    {t('business.document_vault.field.end_date', 'End date')}
                  </label>
                  <DatePicker
                    id="dv-end-date"
                    selected={form.endDate ? new Date(form.endDate) : null}
                    onChange={(d) => handleField('endDate', d ? d.toISOString().slice(0, 10) : '')}
                    dateFormat="dd/MM/yyyy"
                    className={`form-control ${errors.endDate ? 'is-invalid' : ''}`}
                    wrapperClassName="w-100"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    placeholderText="dd/mm/yyyy"
                    isClearable
                  />
                  {errors.endDate && <div className="text-danger small mt-1">{errors.endDate}</div>}
                  <div className="text-muted small mt-1">
                    <i className="bx bx-bell me-1"></i>
                    {t('business.document_vault.field.reminder_schedule_hint', 'Admins are notified 90/60/30/7 days before and on the expiry date.')}
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="mb-4 g-3">
              <Col md={12}>
                <div className="form-group">
                  <label htmlFor="dv-asset-search" className="form-label fw-medium">
                    {t('business.document_vault.field.assets', 'Assets')}
                  </label>
                  {selectedAssets.length > 0 && (
                    <div className="mb-2 d-flex flex-wrap gap-2">
                      {selectedAssets.map((asset) => (
                        <div
                          key={asset.id}
                          className="form-control d-inline-flex align-items-center justify-content-between"
                          style={{ width: 'auto', cursor: 'pointer' }}
                          onClick={() => handleRemoveAsset(asset.id)}
                        >
                          <span className="me-2">
                            {asset.assetName || asset.name}
                            {asset.serialNumber ? ` - ${asset.serialNumber}` : ''}
                          </span>
                          <FeatherIcon icon="x" size={16} className="text-muted" />
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="position-relative">
                    <Input
                      type="text"
                      id="dv-asset-search"
                      placeholder={t('shared.type_to_search_assets', 'Type to search assets...')}
                      value={assetSearchTerm}
                      onChange={(e) => {
                        setAssetSearchTerm(e.target.value);
                        setShowAssetDropdown(e.target.value.length > 0);
                      }}
                      onFocus={() => assetSearchTerm.length > 0 && setShowAssetDropdown(true)}
                      onBlur={() => setTimeout(() => setShowAssetDropdown(false), 200)}
                    />
                    {showAssetDropdown && assets.length > 0 && (
                      <div
                        className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm"
                        style={{ zIndex: 1050, maxHeight: 200, overflowY: 'auto', top: '100%' }}
                      >
                        {assets.map((asset) => (
                          <div
                            key={asset.id}
                            role="button"
                            className="p-2 border-bottom d-flex align-items-center"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleSelectAsset(asset)}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                          >
                            <div className="flex-grow-1">
                              <div className="fw-medium text-dark">
                                {asset.assetName || asset.name}{' '}
                                <span className="text-muted">(ID: {asset.id})</span>
                              </div>
                              <small className="text-muted">
                                {asset.serialNumber || t('shared.n_a', 'N/A')}
                                {asset.currentLocation ? ` • ${asset.currentLocation}` : ''}
                              </small>
                            </div>
                            <FeatherIcon icon="chevron-right" size={16} className="text-muted" />
                          </div>
                        ))}
                      </div>
                    )}
                    {showAssetDropdown && assetSearchTerm.length > 0 && assets.length === 0 && (
                      <div
                        className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm p-3 text-center text-muted"
                        style={{ zIndex: 1050, top: '100%' }}
                      >
                        {t('shared.no_assets_found_matching', 'No assets found matching "{{searchTerm}}"', { searchTerm: assetSearchTerm })}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
              <Col md={12}>
                <div className="form-group">
                  <label htmlFor="dv-vendor" className="form-label fw-medium">
                    {t('business.document_vault.field.vendor', 'Vendor')}
                  </label>
                  <Input
                    id="dv-vendor"
                    type="select"
                    value={form.vendorId || ''}
                    onChange={(e) => handleField('vendorId', e.target.value)}
                  >
                    <option value="">{t('shared.none', 'None')}</option>
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.vendorName}
                      </option>
                    ))}
                  </Input>
                </div>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col md={12}>
                <div className="form-group">
                  <label htmlFor="dv-notes" className="form-label fw-medium">
                    {t('business.document_vault.field.notes', 'Notes')}
                  </label>
                  <Input
                    id="dv-notes"
                    type="textarea"
                    rows={3}
                    value={form.notes}
                    onChange={(e) => handleField('notes', e.target.value)}
                  />
                </div>
              </Col>
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeModal} disabled={loading || uploadingFiles}>
            {t('shared.cancel', 'Cancel')}
          </Button>
          <Button color="success" onClick={handleSubmit} disabled={loading || uploadingFiles}>
            {loading ? <Spinner size="sm" /> : form.id ? t('shared.update', 'Update') : t('shared.create', 'Create')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Details modal — gradient-header + info-card pattern (matches ServiceContractsModal) */}
      <Modal
        isOpen={detailsOpen}
        toggle={() => setDetailsOpen(false)}
        size="xl"
        className="modal-dialog-centered"
      >
        <div
          className="modal-header border-0"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '1.5rem 2rem',
          }}
        >
          <div className="w-100">
            <div className="d-flex justify-content-between align-items-start">
              <div className="text-white">
                <h4 className="mb-2 fw-bold">
                  <i className="bx bx-file-blank me-2"></i>
                  {t('business.document_vault.details_title', 'Certificate Details')}
                </h4>
                <div className="d-flex align-items-center gap-3">
                  <span className="badge bg-white bg-opacity-25 text-white">
                    {selectedDocument?.name || t('shared.n_a', 'N/A')}
                  </span>
                  {selectedDocument && renderStatusBadge(selectedDocument)}
                </div>
              </div>
              <div className="d-flex align-items-center gap-3 ms-auto">
                {selectedDocument && (
                  <PermissionGuard permission={DOCUMENT_VAULT_UPDATE}>
                    <button
                      type="button"
                      className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                      onClick={() => {
                        setDetailsOpen(false);
                        openEditModal(selectedDocument);
                      }}
                      style={{
                        background: 'rgba(255, 255, 255, 0.95)',
                        color: '#495057',
                        fontWeight: 500,
                        borderRadius: 8,
                      }}
                    >
                      <FeatherIcon icon="edit-3" size={16} />
                      <span>{t('shared.edit', 'Edit')}</span>
                    </button>
                  </PermissionGuard>
                )}
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setDetailsOpen(false)}
                  aria-label={t('shared.close', 'Close')}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="modal-body p-0" style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {selectedDocument && (
            <div className="p-4">
              {/* Tabs — Info / Assets (mirrors Locations DetailsModal) */}
              <Nav tabs className="nav-tabs-custom mb-0">
                <NavItem>
                  <NavLink
                    className={detailsTab === 'info' ? 'active' : ''}
                    onClick={() => setDetailsTab('info')}
                    style={{ cursor: 'pointer' }}
                  >
                    <FeatherIcon icon="info" size={16} className="me-2" />
                    {t('shared.info', 'Info')}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={detailsTab === 'assets' ? 'active' : ''}
                    onClick={() => setDetailsTab('assets')}
                    style={{ cursor: 'pointer' }}
                  >
                    <FeatherIcon icon="package" size={16} className="me-2" />
                    {t('shared.assets', 'Assets')}
                    {selectedDocument.assets?.length > 0 && (
                      <Badge color="info" pill className="ms-2">
                        {selectedDocument.assets.length}
                      </Badge>
                    )}
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={detailsTab}>
                {/* Info Tab */}
                <TabPane tabId="info">
                  <Card className="border-0 shadow-sm mt-3">
                    <CardBody className="p-4">
                      <Row className="g-4">
                        <Col md={6}>
                          <div className="info-card bg-light rounded p-3 h-100">
                            <h6 className="text-primary mb-3 fw-semibold">
                              <i className="bx bx-info-circle me-2"></i>
                              {t('business.document_vault.section.basics', 'Certificate Information')}
                            </h6>
                            <div className="info-item mb-3">
                              <label className="text-muted small">
                                {t('business.document_vault.field.name', 'Certificate name')}
                              </label>
                              <div className="fw-medium">{selectedDocument.name || t('shared.n_a', 'N/A')}</div>
                            </div>
                            <div className="info-item mb-3">
                              <label className="text-muted small">
                                {t('business.document_vault.field.vendor', 'Vendor')}
                              </label>
                              <div className="fw-medium">
                                {selectedDocument.vendorName || t('shared.n_a', 'N/A')}
                              </div>
                            </div>
                            <div className="info-item mb-0">
                              <label className="text-muted small">{t('shared.status', 'Status')}</label>
                              <div>{renderStatusBadge(selectedDocument)}</div>
                            </div>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="info-card bg-light rounded p-3 h-100">
                            <h6 className="text-success mb-3 fw-semibold">
                              <i className="bx bx-calendar me-2"></i>
                              {t('business.document_vault.section.dates', 'Dates')}
                            </h6>
                            <div className="info-item mb-3">
                              <label className="text-muted small">
                                {t('business.document_vault.field.start_date', 'Start date')}
                              </label>
                              <div className="fw-medium">{formatDateForDisplay(selectedDocument.startDate)}</div>
                            </div>
                            <div className="info-item mb-3">
                              <label className="text-muted small">
                                {t('business.document_vault.field.end_date', 'End date')}
                              </label>
                              <div className="fw-medium">{formatDateForDisplay(selectedDocument.endDate)}</div>
                            </div>
                            <div className="info-item mb-0">
                              <label className="text-muted small">
                                {t('business.document_vault.field.next_reminder', 'Next renewal reminder')}
                              </label>
                              <div className="fw-medium">
                                {selectedDocument.nextReminderDate
                                  ? formatDateForDisplay(selectedDocument.nextReminderDate)
                                  : t('business.document_vault.field.no_upcoming_reminder', 'No upcoming reminder')}
                              </div>
                              <small className="text-muted">
                                {t('business.document_vault.field.reminder_schedule_hint', 'Admins are notified 90/60/30/7 days before and on the expiry date.')}
                              </small>
                            </div>
                          </div>
                        </Col>

                        {selectedDocument.notes && (
                          <Col md={12}>
                            <div className="info-card bg-light rounded p-3">
                              <h6 className="text-info mb-3 fw-semibold">
                                <i className="bx bx-notepad me-2"></i>
                                {t('business.document_vault.field.notes', 'Notes')}
                              </h6>
                              <p className="mb-0 text-muted">{selectedDocument.notes}</p>
                            </div>
                          </Col>
                        )}

                        {selectedDocument.documentUrls && selectedDocument.documentUrls.length > 0 && (
                          <Col md={12}>
                            <div className="info-card bg-light rounded p-3">
                              <h6 className="text-warning mb-3 fw-semibold">
                                <i className="bx bx-paperclip me-2"></i>
                                {t('business.document_vault.field.files', 'Files')}
                              </h6>
                              <div className="d-flex flex-wrap gap-3">
                                {selectedDocument.documentUrls.map((url, index) => {
                                  const isImage = /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?|$)/i.test(url);
                                  const fileName = fileNameFromUrl(url);
                                  return (
                                    <a
                                      key={index}
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="d-block text-decoration-none"
                                    >
                                      {isImage ? (
                                        <img
                                          src={url}
                                          alt={fileName}
                                          className="rounded border"
                                          style={{
                                            width: 150,
                                            height: 150,
                                            objectFit: 'cover',
                                          }}
                                        />
                                      ) : (
                                        <div
                                          className="rounded border d-flex flex-column align-items-center justify-content-center p-3"
                                          style={{
                                            width: 150,
                                            height: 150,
                                            backgroundColor: '#fff',
                                          }}
                                        >
                                          <FeatherIcon
                                            icon={fileIconForUrl(url)}
                                            size={48}
                                            className="mb-2 text-muted"
                                          />
                                          <small
                                            className="text-muted text-center"
                                            style={{ wordBreak: 'break-word', fontSize: 11 }}
                                          >
                                            {fileName.length > 20 ? `${fileName.substring(0, 17)}...` : fileName}
                                          </small>
                                        </div>
                                      )}
                                    </a>
                                  );
                                })}
                              </div>
                            </div>
                          </Col>
                        )}
                      </Row>
                    </CardBody>
                  </Card>
                </TabPane>

                {/* Assets Tab — mirrors Locations DetailsModal renderAssetsTab */}
                <TabPane tabId="assets">
                  <div className="pt-4">
                    <div className="d-flex align-items-center mb-4">
                      <FeatherIcon icon="package" size={20} className="text-primary me-2" />
                      <h5 className="mb-0 fw-bold">{t('shared.assets', 'Assets')}</h5>
                      {selectedDocument.assets?.length > 0 && (
                        <Badge color="info" pill className="ms-2">
                          {selectedDocument.assets.length}
                        </Badge>
                      )}
                    </div>

                    <AssetListTable
                      data={selectedDocument.assets || []}
                      excludeColumns={['location']}
                      onAssetClick={handleAssetClick}
                      emptyMessage={t('shared.no_assets_found', 'No assets found')}
                      emptySubMessage={t('business.document_vault.assets_empty', 'No assets are linked to this document')}
                    />
                  </div>
                </TabPane>
              </TabContent>
            </div>
          )}
        </div>

        <ModalFooter>
          <Button color="secondary" onClick={() => setDetailsOpen(false)}>
            {t('shared.close', 'Close')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete confirmation */}
      <Modal isOpen={deleteOpen} toggle={() => setDeleteOpen(false)} className="modal-dialog-centered">
        <ModalHeader toggle={() => setDeleteOpen(false)}>
          {t('business.document_vault.confirm.delete_title', 'Delete certificate')}
        </ModalHeader>
        <ModalBody>
          {apiError && <Alert color="danger">{apiError}</Alert>}
          <p className="mb-0">
            {t('business.document_vault.confirm.delete', 'Delete this certificate?')}
          </p>
          {selectedDocument?.name && (
            <p className="mt-2 fw-semibold">{selectedDocument.name}</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteOpen(false)} disabled={loading}>
            {t('shared.cancel', 'Cancel')}
          </Button>
          <Button color="danger" onClick={handleDelete} disabled={loading}>
            {loading ? <Spinner size="sm" /> : t('shared.delete', 'Delete')}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Certificates;
